import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaChartBar, FaLaptopCode, FaShieldAlt, FaArrowRight, FaSearch, 
  FaThLarge, FaList, FaPhoneAlt, FaRegCalendarCheck, FaArrowLeft
} from 'react-icons/fa';
import './SolutionsPage.css';

const CATEGORIES = [
  { id: 'all', label: 'All Ecosystem', icon: null },
  { id: 'tally', label: 'Tally ERP Solutions', icon: <FaChartBar /> },
  { id: 'software', label: 'Software & Web Dev', icon: <FaLaptopCode /> },
  { id: 'hardware', label: 'Hardware & Security', icon: <FaShieldAlt /> }
];

const SOLUTIONS = [
  {
    id: 'tallyprime',
    label: 'TallyPrime',
    category: 'tally',
    img: '/images/Primelogo.webp',
    desc: 'Full ERP accounting & compliance suite designed to handle accounts, inventory, payroll, and statutory GST compliances with total control and speed.',
    route: '/tallyprime',
    features: ['GST Compliance & e-Invoicing', 'Complete Financial Accounting', 'Multi-currency support', 'Advanced Inventory Tracking'],
    badge: 'ERP Standard',
    accentColor: '#4A90E2'
  },
  {
    id: 'tallyserver',
    label: 'Tally Server',
    category: 'tally',
    img: '/images/Tallyprime-server.png',
    desc: 'Multi-user networked enterprise server edition that offers high performance, robust data integrity, and lightning-fast multi-user access.',
    route: '/TallyPrimeServer',
    features: ['High-Concurrency Data Access', 'Enhanced Data Security & Controls', 'Concurrent Backup Scheduling', 'Zero User Session Delays'],
    badge: 'Enterprise Performance',
    accentColor: '#39B54A'
  },
  {
    id: 'tallycloud',
    label: 'Tally on Cloud',
    category: 'tally',
    img: '/images/tally_cloud.png',
    desc: 'Access your Tally accounting data securely from anywhere, on any device (Mac, Windows, iOS, Android) with guaranteed 99.9% uptime cloud hosting.',
    route: '/TallyAws',
    features: ['24/7 Remote Anywhere Access', 'High Security with TLS/SSL', 'Automatic Backups & Disaster Recovery', 'Device-independent Interface'],
    badge: 'Cloud Powered',
    accentColor: '#00A896'
  },
  {
    id: 'customtdl',
    label: 'Custom TDL',
    category: 'tally',
    img: '/images/product-updates (1).png',
    desc: 'Tally TDL (Tally Definition Language) customization and custom reports built tailored exactly to your unique business flow and reporting requirements.',
    route: '/TallyDefinition',
    features: ['Bespoke Custom Invoice Formats', 'Automated Business Workflows', 'Integration with External Database', 'Advanced Custom Dashboards'],
    badge: 'Tailored Solution',
    accentColor: '#F5A623'
  },
  {
    id: 'tss',
    label: 'TSS (Tally Software Service)',
    category: 'tally',
    img: '/images/tss-logo.png',
    desc: 'Annual subscription service providing access to regular feature updates, online compliance utilities, remote access capabilities, and priority Tally support.',
    route: '/TallySoftware',
    features: ['Statutory Compliance Updates', 'e-Way Bill & e-Invoicing Upgrades', 'Mobile App Report Syncing', 'On-demand Remote Data Sync'],
    badge: 'Support & Updates',
    accentColor: '#D0021B'
  },
  {
    id: 'tallyira',
    label: 'TallyPrime Ira',
    category: 'tally',
    img: '/images/tally/Blog_Effortless-e-Invoicing-with-TallyPrimes-e-Tims-solution-2.jpg',
    desc: 'AI-powered document ingestion that automates invoice parsing and generates ready-to-verify draft vouchers in TallyPrime.',
    route: '/TallyIra',
    features: ['AI-powered Document Extraction', 'WhatsApp/Email Direct Ingestion', 'Draft Voucher Generation', 'Mobile Document Scanner'],
    badge: 'AI Assistant',
    accentColor: '#0088cc'
  },
  {
    id: 'websites',
    label: 'Websites & Portals',
    category: 'software',
    img: '/images/webapp.png',
    desc: 'Custom web portal development, progressive web apps, corporate showcases, and high-performance APIs tailored for modern businesses.',
    route: '/WebDevelopment',
    features: ['Full-stack Modern Frameworks', 'SEO & Performance Optimized', 'Custom Database Integration', 'Intuitive Dashboard Panels'],
    badge: 'Custom Web',
    accentColor: '#8B5CF6'
  },
  {
    id: 'mobileapps',
    label: 'Mobile Applications',
    category: 'software',
    img: '/images/mobile-app.webp',
    desc: 'Stunning native and cross-platform (Android & iOS) mobile applications designed with fluid UI animations and robust offline caching capacities.',
    route: '/MobileDeveloper',
    features: ['Cross-platform Flutter / React Native', 'Real-time Push Notifications', 'Secure Offline Storage', 'Interactive Charts & Dashboards'],
    badge: 'iOS & Android',
    accentColor: '#EC4899'
  },
  {
    id: 'bizanalyst',
    label: 'Biz Analyst',
    category: 'software',
    img: '/images/logotally/unnamed.png',
    desc: 'Powerful business intelligence dashboard linked with Tally, providing real-time sales, inventory, and ledger outstanding reports on your mobile phone.',
    route: '/BizAnalyst',
    features: ['Live Tally Data Syncing', 'Automatic Payment Reminders', 'Sales Team Performance Tracking', 'Encrypted Cloud Architecture'],
    badge: 'Mobile BI',
    accentColor: '#3B82F6'
  },
  {
    id: 'ecommerce',
    label: 'E-Commerce Platforms',
    category: 'software',
    img: '/images/4861083.jpg',
    desc: 'Complete high-conversion online store setups, multi-vendor marketplaces, shipping integrations, and custom inventory synchronizations.',
    route: '/WebDevelopment',
    features: ['Secure Stripe/Paypal Payment Gateways', 'Dynamic Cart & Checkout Flows', 'Tally Inventory Synchronization', 'Advanced Analytics Dashboard'],
    badge: 'Online Shop',
    accentColor: '#F59E0B'
  },
  {
    id: 'customerp',
    label: 'Custom ERP Solutions',
    category: 'software',
    img: '/images/1232.png',
    desc: 'Fully bespoke web-based ERP systems built for manufacturing, retail, logistics, and trading operations, helping automate complete operational pipelines.',
    route: '/WebDevelopment',
    features: ['Multi-department Workflows', 'Dynamic User Access Controls', 'Production Planning & Scheduling', 'Comprehensive Business Analytics'],
    badge: 'Enterprise Suite',
    accentColor: '#10B981'
  },
  {
    id: 'biometrics',
    label: 'Biometrics & Attendance',
    category: 'hardware',
    img: '/images/GeotimeMar18_2_finger_web_4ff55f3b-97eb-4f7a-b4a8-a98c7f136383_1350x-removebg-preview (1).png',
    desc: 'High-end fingerprint, face recognition, RFID access control, and time-attendance machines integrated with automated cloud payroll systems.',
    route: '/BIO',
    features: ['Sub-second Match Recognition', 'Cloud API Attendance Syncing', 'Anti-spoofing Face Authentication', 'Access Control Door Locks'],
    badge: 'Access Security',
    accentColor: '#6366F1'
  },
  {
    id: 'cctv',
    label: 'CCTV Security Systems',
    category: 'hardware',
    img: '/images/shopping.webp',
    desc: 'IP and analog video surveillance systems, high-definition night-vision cameras, network video recorders (NVR), and remote smartphone live view.',
    route: '/SecuritySystem',
    features: ['HD Night Vision & ColorVu Support', 'AI Motion & Intrusion Alerts', 'Cross-device Remote Live Stream', 'Scalable NVR Network Storage'],
    badge: 'Surveillance',
    accentColor: '#EF4444'
  },
  {
    id: 'servers',
    label: 'Enterprise Servers',
    category: 'hardware',
    img: '/images/servers.jpg',
    desc: 'Premium high-performance server hardware configuration, rack cabinets, virtualized virtualization clusters, and high-availability setups.',
    route: '/ServerPage',
    features: ['Dual Intel Xeon / AMD EPYC Power', 'RAID Enterprise Storage Protection', 'VMware / Hyper-V Virtualization', 'ECC High-Reliability RAM'],
    badge: 'Core Infrastructure',
    accentColor: '#14B8A6'
  },
  {
    id: 'hardwares',
    label: 'Hardware Solutions',
    category: 'hardware',
    img: '/images/71QtoB1MJIL-removebg-preview.png',
    desc: 'End-to-end IT hardware supplies including business workstations, laptops, networking routers, enterprise firewalls, and secure VPN routing.',
    route: '/HardwareServices',
    features: ['High-Performance Business Workstations', 'Managed Switches & Structured Cabling', 'Next-Gen Firewall Security', 'Corporate Laptop Fleet Leasing'],
    badge: 'IT Workstations',
    accentColor: '#6B7280'
  },
  {
    id: 'printers',
    label: 'Professional Printers',
    category: 'hardware',
    img: '/images/printer.webp',
    desc: 'Industrial barcode printers, thermal receipt machines, heavy-duty laser workgroups, and integrated scanner solutions for logistics.',
    route: '/PrinterSoftware',
    features: ['Industrial Thermal Barcode Printers', 'High-Speed Workgroup Laser Jets', 'Automatic Duplex Scanning Units', 'Low-maintenance OEM Consumables'],
    badge: 'Office Document',
    accentColor: '#84CC16'
  }
];

export default function SolutionsPage() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'

  const filteredSolutions = useMemo(() => {
    return SOLUTIONS.filter((item) => {
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      const matchesSearch = item.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            item.desc.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            item.features.some(f => f.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  return (
    <div className="solutions-page-wrapper">
      {/* ── Background Aesthetics ── */}
      <div className="solutions-bg-gradient" aria-hidden="true" />
      <div className="solutions-glow-orb-1" aria-hidden="true" />
      <div className="solutions-glow-orb-2" aria-hidden="true" />

      <div className="solutions-page-container">
        
        {/* ── Breadcrumbs & Back Navigation ── */}
        <div className="solutions-navigation">
          <button className="back-home-btn" onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to Home
          </button>
        </div>

        {/* ── Header Showcase Section ── */}
        <header className="solutions-header-modern">
          <div className="solutions-top-badge">
            <span className="badge-pulse" /> iMatrix Tech Ecosystem
          </div>
          <h1>Explore Our Enterprise Solutions</h1>
          <p className="solutions-subtitle">
            From world-class Tally ERP suites to custom software, mobile apps, and robust hardware configurations—we supply and customize the digital infrastructure that fuels growth.
          </p>
        </header>

        {/* ── Interactive Filters and Controls Panel ── */}
        <div className="solutions-controls-panel">
          
          {/* Category Tabs */}
          <div className="category-tabs-container">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                className={`category-tab-btn ${selectedCategory === cat.id ? 'category-tab-btn--active' : ''}`}
                onClick={() => setSelectedCategory(cat.id)}
              >
                {cat.icon && <span className="tab-icon">{cat.icon}</span>}
                <span className="tab-label">{cat.label}</span>
              </button>
            ))}
          </div>

          {/* Search & View Switcher */}
          <div className="controls-right-group">
            <div className="search-input-wrapper">
              <FaSearch className="search-magnifier" />
              <input
                type="text"
                placeholder="Search solutions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="view-mode-toggle">
              <button
                className={`view-btn ${viewMode === 'grid' ? 'view-btn--active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid View"
                aria-label="Grid View"
              >
                <FaThLarge />
              </button>
              <button
                className={`view-btn ${viewMode === 'list' ? 'view-btn--active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List View"
                aria-label="List View"
              >
                <FaList />
              </button>
            </div>
          </div>
        </div>

        {/* ── Solutions Display Area ── */}
        <AnimatePresence mode="wait">
          {filteredSolutions.length > 0 ? (
            <motion.div
              key={`${viewMode}-${selectedCategory}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={viewMode === 'grid' ? 'solutions-grid-layout' : 'solutions-list-layout'}
            >
              {filteredSolutions.map((solution, idx) => (
                <motion.div
                  key={solution.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.25, delay: Math.min(idx * 0.02, 0.12) }}
                  className={`solution-card-premium solution-card-premium--${solution.category}`}
                  style={{ '--accent-glow': solution.accentColor }}
                  onClick={() => navigate(solution.route)}
                >
                  
                  {/* Card Visual / Image Section */}
                  <div className="card-visual-section">
                    <img src={solution.img} alt={solution.label} loading="lazy" />
                    <span className="solution-type-badge">{solution.badge}</span>
                    <div className="card-image-gradient" />
                  </div>

                  {/* Card Content Section */}
                  <div className="card-content-section">
                    <div className="card-heading-row">
                      <h2>{solution.label}</h2>
                      <div className="category-mini-badge">
                        {solution.category === 'tally' && <FaChartBar title="Tally ERP" />}
                        {solution.category === 'software' && <FaLaptopCode title="Software & Web" />}
                        {solution.category === 'hardware' && <FaShieldAlt title="Hardware & Security" />}
                      </div>
                    </div>
                    
                    <p className="solution-description">{solution.desc}</p>

                    {/* Features checklist (Only shown/emphasized in rich layouts) */}
                    <div className="solution-features-list">
                      <h3>Core Highlights:</h3>
                      <ul>
                        {solution.features.map((feature, fIdx) => (
                          <li key={fIdx}>
                            <span className="checkmark">✦</span> {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="card-interactive-footer">
                      <span className="explore-action-link">
                        Explore Solution <FaArrowRight />
                      </span>
                    </div>
                  </div>

                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="no-solutions-fallback"
            >
              <h3>No Solutions Found</h3>
              <p>We couldn't find any services matching your filters or search query. Try tweaking your words!</p>
              <button className="reset-search-btn" onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}>
                Reset All Filters
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Premium Call to Action (CTA) ── */}
        <section className="solutions-page-cta">
          <div className="cta-glass-card">
            <div className="cta-text">
              <h2>Can't find the perfect fit?</h2>
              <p>Our experienced technology consultants can design a bespoke hardware, software, or Tally customization bundle optimized for your business operations.</p>
            </div>
            <div className="cta-actions">
              <a href="tel:+919841609990" className="btn-primary-phone">
                <span className="phone-icon-glow"><FaPhoneAlt /></span>
                <div className="phone-details">
                  <span className="phone-label">Contact Us / Speak to an Expert</span>
                  <span className="phone-number">+91 98416 09990</span>
                </div>
              </a>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}
